import React from 'react';
import { ReloadOutlined } from '@ant-design/icons';
import { Spin, Tooltip } from 'antd';
import { FormItem } from '@xbcb/form-item-components';
import { NamePath } from '@xbcb/ui-types';
import { useTheme } from 'styled-components';
import {
  useCpscCitationCodes,
  CpscCitationCodeType,
} from 'hooks/useCpscCitationCodes';
import {
  StyledSelect,
  StyledLabelWrapper,
  StyledRefreshButton,
} from './styles';

export interface CitationCodesProps {
  fullNamePath: NamePath;
  localNamePath: NamePath;
  codeType: CpscCitationCodeType;
  label?: React.ReactNode;
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
  maxLength?: number;
  required?: boolean;
}

const CitationCodes: React.FC<CitationCodesProps> = ({
  codeType,
  localNamePath,
  label = 'Citation Codes',
  placeholder = 'Select or type citation codes',
  readOnly = false,
  disabled = false,
  maxLength = 72,
  required = false,
}) => {
  const theme = useTheme();
  const { options, loading, refresh } = useCpscCitationCodes(codeType);

  const labelWithRefresh = (
    <StyledLabelWrapper>
      {label}
      {!readOnly && (
        <Tooltip title="Refresh citation codes" className="tooltip">
          <StyledRefreshButton
            className="refresh button"
            type="text"
            size="small"
            icon={loading ? <Spin size="small" /> : <ReloadOutlined />}
            onClick={() => void refresh()}
            disabled={loading || disabled}
            data-testid="citation-codes-refresh"
          />
        </Tooltip>
      )}
    </StyledLabelWrapper>
  );

  return (
    <FormItem
      name={localNamePath}
      label={labelWithRefresh}
      $itemSize={theme.size.medium_large}
      $readOnly={readOnly}
      rules={[{ required }]}
      validateTrigger="onBlur"
    >
      <StyledSelect
        data-testid="citation-codes-select"
        mode="multiple"
        labelInValue
        placeholder={placeholder}
        disabled={disabled || readOnly}
        options={options}
        tokenSeparators={[',']}
        maxTagTextLength={maxLength}
        showSearch
        optionFilterProp="value"
        optionLabelProp="value"
        loading={loading}
        notFoundContent={
          loading ? <Spin size="small" /> : 'No citation codes found'
        }
      />
    </FormItem>
  );
};

export default CitationCodes;
